import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { forOwn } from 'lodash-es';
import { Button, Form } from 'reactstrap';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import {
  FORM_FIELD_CLASS_NAMES,
  FORM_FIELD_NAMES,
  MAX_REVIEW_TEXT_LENGTH,
  MAX_REVIEW_AUTHOR_NAME_LENGTH,
  FIELD_DANGER_CLASS_NAMES,
  FORM_VALIDATORS,
} from 'site-modules/dealerships/constants/dealer-review';
import { Link } from 'site-modules/shared/components/link/link';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { Checkbox } from 'site-modules/shared/components/checkbox/checkbox';
import { EdmundsAPI } from 'client/data/api/api-client';
import { logger } from 'client/utils/logger/logger';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';

import './dealer-review-comment-form.scss';

class DealerReviewCommentFormUI extends Component {
  static propTypes = {
    fields: PropTypes.shape({}).isRequired,
    fieldRef: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
    validationErrors: PropTypes.shape({}),
    onSuccessfulSubmit: PropTypes.func.isRequired,
    reviewId: PropTypes.string.isRequired,
  };

  static defaultProps = {
    validationErrors: {},
  };

  state = {
    isLoading: false,
    isSubmitFailed: false,
    isDealerComment: false,
    showTheForm: true,
    showSuccessMessage: false,
    remainingAuthorNameCharacters: MAX_REVIEW_AUTHOR_NAME_LENGTH,
    remainingCommentCharacters: MAX_REVIEW_TEXT_LENGTH,
  };

  onReviewAuthorNameChange = (event, fieldName) => {
    this.props.validate(event, fieldName);

    this.setState({
      remainingAuthorNameCharacters: MAX_REVIEW_AUTHOR_NAME_LENGTH - event.target.value.length,
    });
  };

  onReviewCommentChange = (event, fieldName) => {
    this.props.validate(event, fieldName);

    this.setState({
      remainingCommentCharacters: MAX_REVIEW_TEXT_LENGTH - event.target.value.length,
    });
  };

  onClick = event => {
    event.preventDefault();

    const { validate } = this.props;

    if (validate && !validate()) {
      return;
    }

    const fieldValues = {};

    forOwn(this.props.fields, ({ value }, name) => {
      fieldValues[name] = value();
    });

    this.onSubmit({ ...fieldValues, isDealerComment: this.state.isDealerComment });
  };

  onSubmit = async values => {
    this.setState({ isLoading: true });
    const dealerReviewsApi = '/dealerreviews/v2/comments/';
    const { comment, authorName, email, isDealerComment } = values;
    const bodyParams = {
      parentId: this.props.reviewId,
      text: comment,
      authorName,
      email,
      dealerComment: isDealerComment,
    };
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyParams),
    };

    try {
      const response = await EdmundsAPI.fetchJson(dealerReviewsApi, options);
      if (response.id) {
        this.props.onSuccessfulSubmit();
        this.setState({ isLoading: false, showTheForm: false, showSuccessMessage: true });
      }
    } catch (error) {
      logger.error(`Dealer review comment submit error - ${error}`);
      this.setState({ isLoading: false, isSubmitFailed: true });
    }
  };

  handleCheckboxClick = () => {
    this.setState(({ isDealerComment }) => ({ isDealerComment: !isDealerComment }));
  };

  componentFieldRef = field => {
    if (!field) {
      return undefined;
    }
    return this.props.fieldRef(field.props.name, field);
  };

  render() {
    const { validate, validationErrors } = this.props;
    const {
      isLoading,
      isSubmitFailed,
      showTheForm,
      showSuccessMessage,
      remainingAuthorNameCharacters,
      remainingCommentCharacters,
    } = this.state;

    const { COMMENT, AUTHOR_NAME, EMAIL } = FORM_FIELD_NAMES;
    const isCommentError = COMMENT in validationErrors;
    const isAuthorNameError = AUTHOR_NAME in validationErrors;
    const isEmailError = EMAIL in validationErrors;

    return (
      <>
        {showTheForm && (
          <LoadingSpinner isLoading={isLoading}>
            <Form
              id="dealer-review-comment-form"
              className="dealer-review-comment-form px-1_5 pb-1_5"
              data-test="dealer-form"
            >
              <div className="mb-1_5 pt-1_5">
                <div className="d-flex justify-content-between align-items-baseline">
                  <span className="heading-4">Add a Comment</span>
                </div>
                {isSubmitFailed && (
                  <div className="text-danger fw-bold mt-0_5">
                    Comment submission failed. Please submit your comment again.
                  </div>
                )}
              </div>
              <label className={classnames(FORM_FIELD_CLASS_NAMES.labelClassName)} htmlFor={AUTHOR_NAME}>
                Author Name or Nickname* <span className="fw-normal">(will be published with review)</span>
              </label>
              <p className="small mb-0_5">Only letters, numbers, and spaces will be accepted.</p>
              <FormField
                {...FORM_FIELD_CLASS_NAMES}
                ref={this.componentFieldRef}
                name={AUTHOR_NAME}
                id={AUTHOR_NAME}
                placeholder="Example: John Doe"
                maxLength={MAX_REVIEW_AUTHOR_NAME_LENGTH}
                onBlur={validate}
                onChange={this.onReviewAuthorNameChange}
                isValid={!isAuthorNameError}
                inputClassName="py-0_5"
              />
              <div className="field-danger-wrapper d-flex justify-content-between">
                <div className={FIELD_DANGER_CLASS_NAMES}>{validationErrors[AUTHOR_NAME]}</div>
                <div className="small text-gray-dark pt-0_25 mb-1_5">
                  Character limit: {remainingAuthorNameCharacters}
                </div>
              </div>
              <FormField
                {...FORM_FIELD_CLASS_NAMES}
                ref={this.componentFieldRef}
                name={EMAIL}
                id={EMAIL}
                label={
                  <Fragment>
                    Email*{' '}
                    <span className="fw-normal">
                      (will <i>not</i> be published with review)
                    </span>
                  </Fragment>
                }
                placeholder="Enter email address"
                type="email"
                autoComplete="email"
                onBlur={validate}
                onChange={validate}
                isValid={!isEmailError}
                inputClassName="py-0_5"
              />
              <div className={classnames(FIELD_DANGER_CLASS_NAMES, { 'mb-1_5': isEmailError })}>
                {validationErrors[EMAIL]}
              </div>
              <FormField
                {...FORM_FIELD_CLASS_NAMES}
                tag="textarea"
                rows={3}
                ref={this.componentFieldRef}
                name={COMMENT}
                id={COMMENT}
                label="Post a comment*"
                placeholder="Enter comment for the review above"
                onBlur={validate}
                onChange={this.onReviewCommentChange}
                isValid={!isCommentError}
                inputClassName="py-0_5"
                maxLength={MAX_REVIEW_TEXT_LENGTH}
              />
              <div className="field-danger-wrapper d-flex justify-content-between">
                <div className={FIELD_DANGER_CLASS_NAMES}>{validationErrors[COMMENT]}</div>
                <div className="small text-gray-dark pt-0_25 mb-1_5">Character limit: {remainingCommentCharacters}</div>
              </div>
              <Checkbox
                labelText="I am responding on behalf of the dealership."
                id="dealer-comment"
                onClick={this.handleCheckboxClick}
                containerClassName="text-gray-darkest mb-1"
                textLabelClassName="ps-1_5"
              />
              <div className="text-gray-darker" data-test="legal-copy">
                Please review your comments carefully before submitting. Once submitted, they cannot be deleted or
                changed. All reviews and comments to Edmunds are subject to the terms of our{' '}
                <Link
                  to="/about/visitor-agreement.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-underline"
                >
                  Visitor Agreement
                </Link>
                .
              </div>
              <Button
                type="submit"
                color="primary-b"
                size="lg"
                className="btn-step-submit text-transform-none my-1_5 py-0_5 px-1"
                onClick={this.onClick}
                data-tracking-id="dealer_review_comment_submit"
                data-test="submit-btn"
              >
                <span className="fw-normal">Post Comment</span>
              </Button>
            </Form>
            <hr className="mt-0 mb-2" />
          </LoadingSpinner>
        )}
        {showSuccessMessage && (
          <div>
            <h4>Thanks for your comment!</h4>
            <hr className="my-1_5" />
          </div>
        )}
      </>
    );
  }
}

export const DealerReviewCommentForm = formValidation(DealerReviewCommentFormUI, FORM_VALIDATORS);
